// eslint-disable-next-line no-unused-vars
import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { bool, func, object } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Button, Loading, SkeletonPlaceholder, SkeletonText } from '@carbon/react';
import { Trash, Upload } from 'lucide-react';

import { NWTextInput } from 'Components/shared';

const StyledAccountCompanyData = styled.form`
  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4 {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 16px;
      background: #f5f7f9;
      font-size: 16px;
    }

    .confidential {
      background: #edf8f5;
      border: 1px solid #73b5a5;
      border-radius: 999px;
      color: #236658;
      font-size: 10px;
      font-weight: 500;
      padding: 2px 6px;
    }

    .sectionBody {
      padding: 16px;
    }
  }

  .description {
    margin-bottom: 24px;
    color: ${ props => props.theme.colors.darkGray };
    font-size: 14px;
  }

  .logoSection {
    display: grid;
    grid-template-columns: minmax(220px, 280px) 1fr;
    gap: 48px;
    align-items: center;
  }

  .logoPreview {
    display: flex;
    width: 100%;
    min-height: 140px;
    padding: 16px;
    border: 1px dashed #8d8d8d;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      max-height: 110px;
      object-fit: contain;
    }

    strong {
      color: #183866;
      font-size: 20px;
      letter-spacing: 0.02em;
    }

    span {
      margin-top: 8px;
      color: #6f6f6f;
      font-size: 12px;
    }
  }

  .logoDetails {
    h5 {
      margin-bottom: 8px;
      font-size: 16px;
    }

    p {
      max-width: 540px;
      margin-bottom: 20px;
      color: ${ props => props.theme.colors.darkGray };
      font-size: 14px;
      line-height: 1.45;
    }
  }

  .logoActions {
    display: flex;
    gap: 16px;

    button {
      min-width: 180px;
      height: 40px !important;

      svg {
        width: 16px;
        height: 16px;
        margin-right: 12px;
      }
    }

    .uploadButton {
      background: #da1e28;
    }

    .removeButton {
      border-color: #1f59a2;
      color: #1f59a2;
    }
  }

  .logoError {
    margin-top: 8px;
    color: #da1e28;
    font-size: 12px;
  }

  .fieldsGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;

    .fullWidth {
      grid-column: 1 / -1;
    }
  }

  .submit {
    display: flex;
    justify-content: flex-end;

    button {
      width: 100%;
      max-width: 255px;
      height: 40px !important;
      background: #da1e28;
    }
  }

  .skeletonBody {
    display: grid;
    gap: 16px;
  }

  .skeletonField {
    width: 100%;
    height: 48px;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .logoSection,
    .fieldsGrid {
      grid-template-columns: 1fr;
    }

    .logoSection {
      gap: 24px;
    }

    .fieldsGrid .fullWidth {
      grid-column: auto;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    .logoActions {
      flex-direction: column;

      button {
        width: 100%;
      }
    }

    .submit button {
      max-width: none;
    }
  }
`;

const fields = {
  resellerName: 'resellerName',
  resellerTaxIdentificationNumber: 'resellerTaxIdentificationNumber',
  resellerRegistrationNumber: 'resellerRegistrationNumber',
  resellerHeadquartersAddress: 'resellerHeadquartersAddress',
  resellerCompanyEmail: 'resellerCompanyEmail',
  resellerPhone: 'resellerPhone',
};

const AccountCompanyData = ({
  loading,
  validators,
  register,
  rhfErrors,
  logo,
  handleLogoUpload,
  handleLogoRemove,
  handleOnSubmit,
  submitLoading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, validators[fieldName]),
  });

  return (
    <StyledAccountCompanyData theme={ theme } onSubmit={ handleOnSubmit } noValidate>
      { loading ? (
        <section>
          <h4><SkeletonText /></h4>
          <div className="sectionBody skeletonBody">
            <SkeletonText paragraph lineCount={ 2 } />
            <SkeletonPlaceholder className="skeletonField" />
            <SkeletonPlaceholder className="skeletonField" />
            <SkeletonPlaceholder className="skeletonField" />
          </div>
        </section>
      ) : (
        <>
          <section>
            <h4>
              { t('offers.companyDataHeader') }
              <span className="confidential">{ t('offers.companyDataExportOnly') }</span>
            </h4>
            <div className="sectionBody">
              <p className="description">{ t('offers.companyDataDescription') }</p>
              <div className="logoSection">
                <div className="logoPreview">
                  { logo?.dataUrl ? (
                    <img src={ logo.dataUrl } alt={ t('offers.companyLogo') } />
                  ) : (
                    <>
                      <strong>{ t('offers.companyLogoPlaceholder') }</strong>
                      <span>{ t('offers.companyLogoFormats') }</span>
                    </>
                  ) }
                </div>
                <div className="logoDetails">
                  <h5>{ t('offers.companyLogo') }</h5>
                  <p>{ t('offers.companyLogoDescription') }</p>
                  <input
                    ref={ fileInputRef }
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    hidden
                    onChange={ event => {
                      handleLogoUpload(event.target.files?.[0]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    } }
                  />
                  <div className="logoActions">
                    <Button
                      type="button"
                      className="uploadButton"
                      onClick={ () => fileInputRef.current?.click() }
                    >
                      <Upload />
                      { t('offers.uploadCompanyLogo') }
                    </Button>
                    <Button
                      type="button"
                      kind="ghost"
                      className="removeButton"
                      onClick={ handleLogoRemove }
                    >
                      <Trash />
                      { t('offers.removeCompanyLogo') }
                    </Button>
                  </div>
                  { rhfErrors.logo?.message && (
                    <p className="logoError" role="alert">{ rhfErrors.logo.message }</p>
                  ) }
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4>
              { t('offers.companyInformation') }
              <span className="confidential">{ t('offers.companyDataExportOnly') }</span>
            </h4>
            <div className="sectionBody fieldsGrid">
              <NWTextInput
                className="fullWidth"
                light
                disabled
                labelText={ t('offers.companyName') }
                { ...sharedProps(fields.resellerName) }
              />
              <NWTextInput
                light
                labelText={ t('offers.companyTaxIdentificationNumber') }
                { ...sharedProps(fields.resellerTaxIdentificationNumber) }
              />
              <NWTextInput
                light
                labelText={ t('offers.companyTradeRegisterNumber') }
                { ...sharedProps(fields.resellerRegistrationNumber) }
              />
              <NWTextInput
                className="fullWidth"
                light
                labelText={ t('offers.companyAddress') }
                { ...sharedProps(fields.resellerHeadquartersAddress) }
              />
              <NWTextInput
                light
                type="email"
                labelText={ t('offers.companyEmail') }
                { ...sharedProps(fields.resellerCompanyEmail) }
              />
              <NWTextInput
                light
                type="tel"
                labelText={ t('offers.companyPhone') }
                { ...sharedProps(fields.resellerPhone) }
              />
            </div>
          </section>
        </>
      ) }

      <div className="submit">
        <Button type="submit" className="primary" disabled={ loading || submitLoading }>
          { submitLoading && <Loading active small withOverlay={ false } /> }
          { t('offers.saveCompanyData') }
        </Button>
      </div>
    </StyledAccountCompanyData>
  );
};

AccountCompanyData.propTypes = {
  loading: bool,
  validators: object,
  register: func,
  rhfErrors: object,
  logo: object,
  handleLogoUpload: func,
  handleLogoRemove: func,
  handleOnSubmit: func,
  submitLoading: bool,
};

AccountCompanyData.displayName = 'AccountCompanyDataComponent';

export default AccountCompanyData;
